/**
 * LR_Cross — a closed candle crosses the LR body. One file, NO IMPORTS.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE RULE (owner's decisions, 2026-11-01)
 *   The LR body is the CLN linear-regression body the chart's LR chip draws:
 *   upper = max(linreg(open,N), linreg(close,N)), lower = min(...), regressed
 *   over the RAW candles on every candle type. N is the CHART's LR length
 *   (`settings.candle.clnLength`) — there is no separate dial, so every mark sits
 *   on a body the user can see.
 *
 *   The SOURCE close is the Regular close or the app's Heikin-Ashi close
 *   (EMA3 of OHLC4, `transformCandles(..., 'HA')`), chosen by the user.
 *
 *     above[i] = src[i] > upper[i]        below[i] = src[i] < lower[i]
 *     BUY   on the FIRST closed bar above:  !above[i-1] && above[i]
 *     SHORT on the FIRST closed bar below:  !below[i-1] && below[i]
 *
 *   Each bar is compared with ITS OWN body (owner's choice over the previous
 *   bar's). The body at bar i is fitted on bars <= i, so a CLOSED bar's verdict
 *   never moves; callers must pass closed bars only.
 *
 *   EXITS. A long is exited when the source closes below the LOWER edge — which
 *   is, by construction, exactly the bar the SHORT fires on (a bar cannot close
 *   below the lower edge while long without crossing it). So an exit is never a
 *   separate signal: the SHORT step carries `closes: 'LONG'` and the alert reads
 *   "SHORT ✕L". Signals stay BUY | SHORT only, which keeps the SignalLog's
 *   forward tracking (freezePriorActiveSignals) correct unchanged.
 *
 *   A cross in the direction already held (price fell back into the body and
 *   crossed out the same way again) still emits — the owner asked for every
 *   first cross — and is flagged `reCross` so the text can say so.
 *
 *   No stop, no target, no auto-trade: alerts only (lrCrossScan.service).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY NO IMPORTS
 *   Byte-identical twin, guarded by `node scripts/check-engine-parity.mjs`:
 *       frontend/src/utils/strategies/lrCrossEngine.ts     <- source of truth
 *       backend/src/services/strategies/lrCrossEngine.ts
 *   The LR bodies and the source closes are ARGUMENTS, so the chart and the
 *   scheduled scan decide every bar with the same bytes.
 *
 * WARM-UP. `linreg` fits a partial window early on and the HA close is an EMA
 *   with a recursive open, so the first bars of any window differ between a
 *   chart (long history) and a scan (shorter window). Nothing is emitted before
 *   `lrCrossWarmup(N)` = max(N, 30) — by then the regression windows of the bar
 *   and its predecessor are full and the HA recursion has converged far below a
 *   price tick (its weight halves every bar).
 */

export type LrCrossSource = 'Regular' | 'HA';
export type LrCrossAction = 'BUY' | 'SHORT';
export type LrCrossPos = 'LONG' | 'SHORT' | 'FLAT';
/** Reason codes — mapped to plain English in signalLabels.ts. */
export type LrCrossReason = 'LRX_CROSS_UP' | 'LRX_CROSS_DOWN';

/** The chart's default LR length (CLN_DEFAULT_LENGTH), used when no length is given. */
export const LR_CROSS_DEFAULT_LENGTH = 14;
/** Floor for the warm-up so the HA recursion has converged whatever N is. */
export const LR_CROSS_MIN_WARMUP = 30;

/** Option-strategy idea named in the alert text (text only — nothing is armed). */
export const LR_CROSS_OPTION_HINT: Record<LrCrossAction, string> = {
  BUY: 'Bull Put (credit)',
  SHORT: 'Bear Call (credit)',
};

export function resolveLrCrossSource(v: unknown): LrCrossSource {
  return v === 'HA' ? 'HA' : 'Regular';
}

export function resolveLrCrossLength(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) && n >= 2 && n <= 200 ? Math.round(n) : LR_CROSS_DEFAULT_LENGTH;
}

export function lrCrossWarmup(length: number): number {
  return Math.max(resolveLrCrossLength(length), LR_CROSS_MIN_WARMUP);
}

/** The only fields of an LR (CLN) bar the rule reads. `ClnBar` on both sides is
 *  a structural superset, so either side's array can be passed directly. */
export interface LrCrossLrBar {
  time: number;
  lrBodyHigh: number;
  lrBodyLow: number;
}

export interface LrCrossOptions {
  /** The LR length the bodies were computed at — used only for the warm-up. */
  length?: number;
}

export interface LrCrossStep {
  time: number;
  /** This bar's LR body edges — the levels it was judged against. */
  upper: number;
  lower: number;
  /** The source close that was compared (Regular or HA close). */
  src: number;
  signal: LrCrossAction | null;
  reason: LrCrossReason | null;
  /** Running position AFTER this bar's signal. */
  pos: LrCrossPos;
  /** The opposite position this signal also exits ("✕L" / "✕S"), or null. */
  closes: 'LONG' | 'SHORT' | null;
  /** True when the signal points the way the position already is. */
  reCross: boolean;
}

/**
 * Walk the bars and emit one signal per first cross.
 * `lr` and `src` are index-aligned, ascending, CLOSED bars only.
 */
export function computeLrCrossSteps(
  lr: ReadonlyArray<LrCrossLrBar>,
  src: ReadonlyArray<number>,
  opts: LrCrossOptions = {},
): LrCrossStep[] {
  const steps: LrCrossStep[] = [];
  const n = lr && src ? Math.min(lr.length, src.length) : 0;
  if (!n) return steps;
  const warm = lrCrossWarmup(opts.length === undefined ? LR_CROSS_DEFAULT_LENGTH : opts.length);

  let pos: LrCrossPos = 'FLAT';
  for (let i = 0; i < n; i++) {
    const b = lr[i];
    const s = src[i];
    let signal: LrCrossAction | null = null;
    let reason: LrCrossReason | null = null;
    let closes: 'LONG' | 'SHORT' | null = null;
    let reCross = false;

    if (i >= warm && i >= 1) {
      const p = lr[i - 1];
      const sp = src[i - 1];
      const aboveNow = s > b.lrBodyHigh;
      const belowNow = s < b.lrBodyLow;
      const abovePrev = sp > p.lrBodyHigh;
      const belowPrev = sp < p.lrBodyLow;
      // A bar cannot be both above the upper edge and below the lower one
      // (upper >= lower), so at most one of these is true.
      if (aboveNow && !abovePrev) {
        signal = 'BUY'; reason = 'LRX_CROSS_UP';
      } else if (belowNow && !belowPrev) {
        signal = 'SHORT'; reason = 'LRX_CROSS_DOWN';
      }
      if (signal) {
        const next: LrCrossPos = signal === 'BUY' ? 'LONG' : 'SHORT';
        if (pos === next) reCross = true;
        else if (pos !== 'FLAT') closes = pos;
        pos = next;
      }
    }

    steps.push({ time: b.time, upper: b.lrBodyHigh, lower: b.lrBodyLow, src: s, signal, reason, pos, closes, reCross });
  }
  return steps;
}

/** "BUY ✕S" / "SHORT" / "BUY · re-cross" — the action label used in the alert
 *  title, Telegram text and chart notes, so all three say the same thing. */
export function lrCrossActionLabel(step: Pick<LrCrossStep, 'signal' | 'closes' | 'reCross'>): string {
  if (!step.signal) return '';
  if (step.closes) return step.signal + ' ✕' + (step.closes === 'LONG' ? 'L' : 'S');
  if (step.reCross) return step.signal + ' · re-cross';
  return step.signal;
}
